import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Button, Chip, IconButton, Tooltip, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Select, FormControl, InputLabel, Switch, FormControlLabel,
  CircularProgress, Alert
} from '@mui/material';
import { Add, Edit, PersonOff, Person, Key } from '@mui/icons-material';
import { API } from '../context/AuthContext';
import { useAuth } from '../context/AuthContext';
import { useSnackbar } from 'notistack';
import { fmt } from '../utils';

const EMPTY_FORM = { name: '', email: '', password: '', role: 'vendedor', active: true };

function UserDialog({ open, user, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(user);

  useEffect(() => {
    if (open) {
      setForm(user ? { name: user.name, email: user.email, password: '', role: user.role, active: user.active } : EMPTY_FORM);
      setError('');
    }
  }, [open, user]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.email) return setError('Nombre y email son requeridos');
    if (!isEdit && !form.password) return setError('La contraseña es requerida');
    if (form.password && form.password.length < 6) return setError('La contraseña debe tener al menos 6 caracteres');
    setSaving(true); setError('');
    try {
      const payload = { name: form.name, email: form.email, role: form.role, active: form.active };
      if (form.password) payload.password = form.password;
      if (isEdit) {
        await API.put(`/auth/users/${user._id}`, payload);
      } else {
        await API.post('/auth/register', { ...payload, password: form.password });
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {isEdit ? 'Editar usuario' : 'Nuevo usuario'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="Nombre completo" value={form.name} onChange={e => set('name', e.target.value)} fullWidth />
        <TextField label="Email" type="email" value={form.email} onChange={e => set('email', e.target.value)} fullWidth />
        <TextField
          label={isEdit ? 'Nueva contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
          type="password" value={form.password}
          onChange={e => set('password', e.target.value)} fullWidth
        />
        <FormControl fullWidth>
          <InputLabel>Rol</InputLabel>
          <Select value={form.role} label="Rol" onChange={e => set('role', e.target.value)}>
            <MenuItem value="admin">Administrador</MenuItem>
            <MenuItem value="vendedor">Vendedor</MenuItem>
          </Select>
        </FormControl>
        {isEdit && (
          <FormControlLabel
            control={<Switch checked={form.active} onChange={e => set('active', e.target.checked)} color="success" />}
            label={form.active ? 'Usuario activo' : 'Usuario inactivo'}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : isEdit ? 'Guardar cambios' : 'Crear usuario'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, user: null });

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/auth/users/all');
      setUsers(data);
    } catch { enqueueSnackbar('Error al cargar usuarios', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [enqueueSnackbar]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const toggleActive = async (user) => {
    if (user._id === me?.id) return enqueueSnackbar('No puedes desactivar tu propia cuenta', { variant: 'warning' });
    try {
      await API.put(`/auth/users/${user._id}`, { active: !user.active });
      enqueueSnackbar(user.active ? 'Usuario desactivado' : 'Usuario activado', { variant: 'info' });
      fetchUsers();
    } catch (err) { enqueueSnackbar(err.response?.data?.error || 'Error', { variant: 'error' }); }
  };

  const handleSave = () => {
    setDialog({ open: false, user: null });
    fetchUsers();
    enqueueSnackbar('Usuario guardado correctamente', { variant: 'success' });
  };

  const isAdmin = me?.role === 'admin';

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={800} color="#1E1B2E">Usuarios</Typography>
          <Typography variant="body2" color="text.secondary">{users.length} usuarios registrados</Typography>
        </Box>
        {isAdmin && (
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialog({ open: true, user: null })}>
            Nuevo usuario
          </Button>
        )}
      </Box>

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 3 }}>Solo los administradores pueden gestionar usuarios.</Alert>
      )}

      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Usuario</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Rol</TableCell>
              <TableCell>Creado</TableCell>
              <TableCell>Estado</TableCell>
              {isAdmin && <TableCell align="right">Acciones</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
            ) : users.map(u => (
              <TableRow key={u._id} hover sx={{ opacity: u.active ? 1 : 0.55 }}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{
                      width: 36, height: 36, fontSize: '0.85rem', fontWeight: 700,
                      background: u._id === me?.id
                        ? 'linear-gradient(135deg, #9333EA, #06B6D4)'
                        : 'linear-gradient(135deg, #6B7280, #374151)'
                    }}>
                      {u.name.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {u.name}
                        {u._id === me?.id && (
                          <Chip label="Tú" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} color="primary" />
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{u.email}</Typography></TableCell>
                <TableCell>
                  <Chip
                    label={u.role === 'admin' ? 'Administrador' : 'Vendedor'}
                    size="small"
                    color={u.role === 'admin' ? 'secondary' : 'default'}
                    variant={u.role === 'admin' ? 'filled' : 'outlined'}
                  />
                </TableCell>
                <TableCell><Typography variant="body2" color="text.secondary">{fmt.date(u.createdAt)}</Typography></TableCell>
                <TableCell>
                  <Chip
                    label={u.active ? 'Activo' : 'Inactivo'}
                    size="small"
                    color={u.active ? 'success' : 'default'}
                  />
                </TableCell>
                {isAdmin && (
                  <TableCell align="right">
                    <Tooltip title="Editar usuario">
                      <IconButton size="small" onClick={() => setDialog({ open: true, user: u })}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={u.active ? 'Desactivar' : 'Activar'}>
                      <IconButton
                        size="small"
                        color={u.active ? 'error' : 'success'}
                        onClick={() => toggleActive(u)}
                        disabled={u._id === me?.id}
                      >
                        {u.active ? <PersonOff fontSize="small" /> : <Person fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <UserDialog
        open={dialog.open}
        user={dialog.user}
        onClose={() => setDialog({ open: false, user: null })}
        onSave={handleSave}
      />
    </Box>
  );
}
